import React, { useState, useEffect } from 'react';
import { Shield, AlertTriangle, Clock, Globe, TrendingUp, Loader2, RefreshCw } from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';

const AbuseIPDBEnrichment = ({ ipAddress }) => {
  const { getToken } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (ipAddress) {
      fetchAbuseIPDBData();
    }
  }, [ipAddress]);

  const fetchAbuseIPDBData = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = await getToken();
      const response = await fetch(`/api/abuseipdb/check/${ipAddress}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch data');
      }

      const result = await response.json();
      setData(result);
    } catch (err) {
      console.error('AbuseIPDB fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getConfidenceColor = (score) => {
    if (score >= 75) return 'text-red-600 bg-red-50';
    if (score >= 50) return 'text-orange-600 bg-orange-50';
    if (score >= 25) return 'text-yellow-600 bg-yellow-50';
    return 'text-green-600 bg-green-50';
  };

  const getConfidenceBadge = (score) => {
    if (score >= 75) return { text: 'High Risk', color: 'bg-red-600' };
    if (score >= 50) return { text: 'Medium Risk', color: 'bg-orange-600' };
    if (score >= 25) return { text: 'Low Risk', color: 'bg-yellow-600' };
    return { text: 'Clean', color: 'bg-green-600' };
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-slate-200 p-6">
        <div className="flex items-center justify-center space-x-2 text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Loading AbuseIPDB data...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border border-slate-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 text-red-600">
            <AlertTriangle className="w-5 h-5" />
            <span>Error: {error}</span>
          </div>
          <button
            onClick={fetchAbuseIPDBData}
            className="flex items-center space-x-1 px-3 py-1 text-sm text-purple-600 hover:bg-purple-50 rounded-md transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Retry</span>
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const badge = getConfidenceBadge(data.abuseConfidenceScore);

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Shield className="w-5 h-5 text-purple-600" />
          <h3 className="text-lg font-semibold text-slate-900">AbuseIPDB Intelligence</h3>
        </div>
        {data.cached && (
          <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded">
            Cached
          </span>
        )}
      </div>

      {/* Confidence Score */}
      <div className={`p-4 rounded-lg ${getConfidenceColor(data.abuseConfidenceScore)}`}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium">Abuse Confidence Score</span>
          <span className={`text-xs px-2 py-1 rounded text-white ${badge.color}`}>
            {badge.text}
          </span>
        </div>
        <div className="flex items-end space-x-2">
          <span className="text-3xl font-bold">{data.abuseConfidenceScore}%</span>
          <span className="text-sm mb-1">confidence of abuse</span>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-slate-50 p-4 rounded-lg">
          <div className="flex items-center space-x-2 text-slate-600 mb-1">
            <TrendingUp className="w-4 h-4" />
            <span className="text-sm">Total Reports</span>
          </div>
          <div className="text-2xl font-semibold text-slate-900">
            {data.totalReports || 0}
          </div>
        </div>

        <div className="bg-slate-50 p-4 rounded-lg">
          <div className="flex items-center space-x-2 text-slate-600 mb-1">
            <Clock className="w-4 h-4" />
            <span className="text-sm">Recent Reports</span>
          </div>
          <div className="text-2xl font-semibold text-slate-900">
            {data.numDistinctUsers || 0}
          </div>
          <div className="text-xs text-slate-500 mt-1">distinct users</div>
        </div>
      </div>

      {/* IP Details */}
      <div className="space-y-3 pt-2 border-t border-slate-200">
        <div className="flex items-center justify-between">
          <span className="text-sm text-slate-600">IP Address</span>
          <span className="text-sm font-medium text-slate-900">{data.ipAddress}</span>
        </div>

        {data.isWhitelisted !== undefined && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-600">Whitelisted</span>
            <span className={`text-sm font-medium ${data.isWhitelisted ? 'text-green-600' : 'text-slate-900'}`}>
              {data.isWhitelisted ? 'Yes' : 'No'}
            </span>
          </div>
        )}

        {data.usageType && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-600">Usage Type</span>
            <span className="text-sm font-medium text-slate-900">{data.usageType}</span>
          </div>
        )}

        {data.isp && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-600">ISP</span>
            <span className="text-sm font-medium text-slate-900">{data.isp}</span>
          </div>
        )}

        {data.domain && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-600">Domain</span>
            <span className="text-sm font-medium text-slate-900">{data.domain}</span>
          </div>
        )}

        {data.countryCode && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-600 flex items-center space-x-1">
              <Globe className="w-4 h-4" />
              <span>Country</span>
            </span>
            <span className="text-sm font-medium text-slate-900">
              {data.countryName} ({data.countryCode})
            </span>
          </div>
        )}

        {data.lastReportedAt && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-600">Last Reported</span>
            <span className="text-sm font-medium text-slate-900">
              {new Date(data.lastReportedAt).toLocaleDateString()}
            </span>
          </div>
        )}
      </div>

      {/* Reports Preview */}
      {data.reports && data.reports.length > 0 && (
        <div className="pt-2 border-t border-slate-200">
          <h4 className="text-sm font-medium text-slate-900 mb-2">Recent Reports</h4>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {data.reports.slice(0, 5).map((report, idx) => (
              <div key={idx} className="bg-slate-50 p-3 rounded text-sm">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-slate-600">
                    {new Date(report.reportedAt).toLocaleDateString()}
                  </span>
                  <span className="text-xs font-medium text-purple-600">
                    Categories: {report.categories.join(', ')}
                  </span>
                </div>
                {report.comment && (
                  <p className="text-slate-700 text-xs mt-1">{report.comment}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AbuseIPDBEnrichment;